import { Alert, Box, LinearProgress } from "@mui/material";
import { Attachment, Orientation, PageSize, generateQuoteReportV2 } from "pdf-generator";
import React from "react";
import { MyPaper, paperSizes } from "../../../../components";
import { useAppDispatch, useAppSelector } from "../../../../redux";
import { QuoteSliceInterface, updateQuoteSlice } from "../../../../redux/slices/quote";
import { blobToBase64, fileToBase64 } from "../../../../utils";

import copy from "fast-copy";
import { useLazyGetAttachmentUrlQuery } from "../../../../redux/services";
import { orientationOptions } from "../../../documentTemplates/dashboard/addEditTemplate";
import HTMLViewer from "../../../../components/htmlViewer";

export const CoverPagePreview: React.FC<{ children?: JSX.Element, attachments: any[] }> = (props) => {
    
    const dispatch = useAppDispatch();
    const { quote } = useAppSelector((store) => store);

    const [getAttachmentURL] = useLazyGetAttachmentUrlQuery();
    
    const [pdfInfo, setPdfInfo] = React.useState<string>('');

    const { cover_page_config } = quote as QuoteSliceInterface;

    const generatePreview = async (attachment: Attachment | File) => {

        setPdfInfo('');

        const copyQuote = copy(quote);

        if ((attachment as Attachment)?.url) {
            if (quote.cover_page_config.attachments?.length > 0 && !quote.cover_page_config.attachments?.[0]?.base64) {
                await getAttachmentURL({ id: quote.cover_page_config.attachments?.[0]?._id, object_type: "quote", object_id: quote.id }).unwrap().then(async (url) => {
                    copyQuote.cover_page_config.attachments[0].url = url;
                    await fetch(url).then((res) => {
                        return res.blob()
                    }).then(async (res) => {
                        try {
                            const base64 = await blobToBase64(res) as string;
                            copyQuote.cover_page_config.attachments[0].base64 = base64;
                            copyQuote.cover_page_config.attachments[0].url = base64;
                            dispatch(updateQuoteSlice({ ...quote, ...copyQuote }));
                        } catch (error) {
                            console.error(`Error loading quote logo on preview: ${error}`)
                        }
                    }).catch((error: any) => {
                        console.error(`Error loading quote logo on preview: ${error}`)
                    })
                });
            }
        } else if (attachment as File) {
            try {
                const base64String = await fileToBase64(attachment as File);

                if (copyQuote.cover_page_config.attachments.length > 0) {
                    copyQuote.cover_page_config.attachments[0].url = base64String as string;
                } else {
                    copyQuote.cover_page_config.attachments = [{ url: base64String as string }];
                }
            } catch (error) {
                console.error('Error converting file to base64:', error);
            }
        }

        let info = await generateQuoteReportV2({
            title: quote.name,
            headerTitle: quote.name,
            orientation: quote.orientation?.value as Orientation ?? orientationOptions[0].value as Orientation,
            paperSize: quote.paper_size?.value as PageSize ?? paperSizes[4].value as PageSize,
            quoteDetails: copyQuote,
            showFooter: true,
            footerURL: "#",
            configState: {
                tableHeaderColor: quote.quote_report?.table_header_color ?? "d1c4e9",
                useMBFormatter: quote.quote_report?.use_mb_formatter ?? false,
                include_serial_num: quote?.report_config?.include_serial_num ?? false,
                include_revision_table: quote?.report_config?.include_revision_table ?? false,
            },
            onlyCoverPage: true,
            onlyHTML: true
        });

        setPdfInfo(info);
    };

    React.useEffect(() => {
        const timeout = setTimeout(() => {
            if (cover_page_config.include_cover_page) {
                generatePreview(cover_page_config.attachments?.[0] || props.attachments?.[0] || { url: '/imageNotFound.jpg' });
            }
        }, 2000);

        return () => clearTimeout(timeout);
        // eslint-disable-next-line
    }, [quote, JSON.stringify(cover_page_config)]);

    React.useEffect(() => {
        if (cover_page_config.include_cover_page) {
            generatePreview(cover_page_config.attachments?.[0] || props.attachments?.[0] || { url: '/imageNotFound.jpg' });
        }
        // eslint-disable-next-line
    }, [JSON.stringify(props.attachments)]);

    if (!cover_page_config.include_cover_page) {
        return <MyPaper><Alert severity="info">Enable Cover Page to customize</Alert></MyPaper>
    }

    return (
        (<Box sx={{
            height: "calc(100% - 12px)",
            background: (theme) => theme.palette.background.default,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            padding: 2
        }}>

            {!pdfInfo && <LinearProgress />}

            {pdfInfo && <Box sx={{ height: "100%" }}>
                <HTMLViewer
                    htmlContent={pdfInfo}
                    pageSize={quote.paper_size?.value as PageSize ?? paperSizes[4].value as PageSize}
                    orientation={quote.orientation?.value as Orientation ?? orientationOptions[0].value as Orientation}
                />
            </Box>}

        </Box>)
    );
};